import { createContext, useContext } from 'react';

// wiki 外壳的两个开关。放 context 而不是 props，是因为持有它们的
// （`WikiLayout`）与需要它们的（`TopBar` 里那两个按钮）之间隔着页面组件，
// 而 Inertia 的 layout 函数只拿到 page element、没法往下传 props。
//
// 让每个页面组件把 `<WikiTopBar collapsed=… onToggle=…>` 一路接出来是另一条路，
// 但那意味着四个页面各写一遍同样的转发 —— 而这两个开关跟页面在讲什么毫无关系。
export type WikiShell = {
  // 宽屏：边栏收起（设计稿里那个 «）。收起状态存 localStorage，刷新后还在。
  collapsed: boolean;
  toggleCollapsed: () => void;
  // 窄屏：边栏是抽屉。这一格只在 sm 以下有意义。
  openDrawer: () => void;
  // 去搜索。搜索框内嵌在列表页里，所以这个动作是「（必要时先回列表页，然后）
  // 把光标送进那个框」—— 边栏那一行、⌘K、以及列表页上「摊不下的那些」那句话
  // 里的链接，三个调用方要的都是这一件事。
  //
  // 放在 context 里是因为持有导航时机的是 WikiLayout，而调用方隔着页面组件。
  focusSearch: () => void;

  // 建一页并跳过去。**建页只有这一份实现**（在 `WikiLayout` 里），三个调用方
  // 都调它：列表页顶条的 ＋、边栏那一行、筛选无结果时那个「就用这个词建一页」。
  //
  // 给了 `title` 就用它，落地时光标在**正文**（标题已知）。不给就用 `UNTITLED`
  // 建一页占位标题的，落地时光标在**标题**上并全选 —— 第一个按键就把占位词
  // 替换掉，手感等于一个空标题框。
  //
  // **为什么归外壳**：三个入口里有两个隔着页面组件，而边栏那一行还可能是从别的
  // wiki 页面上点的（那时列表页组件根本没挂载）。散成两份的下场在 2026-09-09
  // 那次报障（任务 #487）里见过：边栏自己写了一遍，于是它与列表页各说各话，
  // 从文章页点它什么都不发生 —— 不报错、不告警。
  // `test/wiki_new_page_flow_test.rb` 钉住这一条。
  startNewPage: (title?: string) => void;
  // 建页在飞。两个调用方拿它禁用按钮（连点会建出两页）。
  newPageBusy: boolean;
  // 建页失败的消息。渲染处在 `WikiLayout`（正文列顶上那条横幅）——
  // 动作是外壳级的，而两个触发点（顶条的 ＋ 在页面里、边栏那一行在边栏里）
  // 没有共同的内联落点，`TopBar` 又是每个页面自己渲染的。
  newPageError: string | null;
  dismissNewPageError: () => void;
};

// 没给标题时用的占位标题。
//
// 送一个真标题而不是空字符串：服务端 `validates :title, presence: true`，空标题
// 存不进去。放宽那条校验才是 Notion 的真模型（空标题 + 各处显示时兜「无标题」），
// 但那要动 model、presenter、MCP 的 wiki_write schema、列表页、边栏、面包屑、
// 回收站、版本页 —— 这一期不做。
//
// 与文章页标题框的 placeholder 共用这一个常量：两处写散了，一页「没写标题」和
// 一页「标题被清空了」看起来就会是两个不同的东西。
export const UNTITLED = '无标题';

const noop = () => {};

// 默认值让 TopBar 在 WikiLayout 之外被渲染时也不炸（比如以后有人在别处复用
// 它）。默认值下两个按钮点了没反应，而不是抛「useContext of undefined」。
export const WikiShellContext = createContext<WikiShell>({
  collapsed: false,
  toggleCollapsed: noop,
  openDrawer: noop,
  focusSearch: noop,
  startNewPage: noop,
  newPageBusy: false,
  newPageError: null,
  dismissNewPageError: noop,
});

export const useWikiShell = () => useContext(WikiShellContext);

// 边栏收起状态的存储键。localStorage 直接用字符串键，全项目没有集中的键表，
// 所以带上前缀防撞。
export const SIDEBAR_COLLAPSED_KEY = 'wiki:sidebarCollapsed';

// 列表页那个搜索框的 id。
//
// 边栏的「搜索」那一行不是独立页面 —— 它的动作是「去列表页，并把光标放进那个
// 框」。于是有两个读者要对同一个字符串达成一致：送光标的（`WikiLayout` 里的
// `focusSearch`）与挂 id 的（`SearchBar`）。散着写就是两处字符串各自记得改，
// 而写错的表现是「点了没反应」——不报错、不告警。
//
// 曾经这儿还有一个 `WIKI_NEW_PAGE_INPUT_ID`（列表页那个内联新建框）。
// 建页改成「点 ＋ 立刻建一页并进那一页的编辑态」之后，标题敲在文章页那个 40px
// 的 input 里，列表页不再有新建框，那个 id 也就没有读者了。
export const WIKI_SEARCH_INPUT_ID = 'wiki-search-input';

// 把光标送进上面那个框。
//
// rAF 一帧：Inertia 的 onFinish 在页面组件挂载之后触发，但 React 的提交与
// 浏览器的下一次绘制之间还有一步，直接 focus 偶尔会落在旧节点上。
export const focusById = (id: string) => {
  requestAnimationFrame(() => document.getElementById(id)?.focus());
};
